import { useEffect } from 'react';
import { RotatingLines } from 'react-loader-spinner';

import { notify } from 'components/ToastNotification/ToastNotification';
import { IconManager } from 'components/_universal/IconManager/IconManager';
import { Tooltip } from 'components/_universal/Tooltip/Tooltip';

import {
    useGetReportLazyQuery,
    useGetReportUrlLazyQuery,
    useRequestReportMutation,
} from 'src/graphql/generated';

import { setFormData, setRecordingId } from 'state/redux/slices/app';
import { dispatch } from 'state/redux/store';
import { ToastTypes } from 'types/CommonTypes';

import useRemoveTypename from 'utils/hooks/useRemoveTypename';
import { FormWrapper } from './Form.styled';
import { THEME_VARIANT } from 'shared/constants';

export interface IProps {
    hasReport: boolean;
    isReportQueued: boolean;
    recordingId: number;
}

export const Form = ({ recordingId, hasReport, isReportQueued }: IProps) => {
    const handleError = (message: string) => (error: any) =>
        notify(`${message}: ${error.message}`)[ToastTypes.ERROR]();

    const [getReportUrl] = useGetReportUrlLazyQuery({
        variables: { recordingId },
        onCompleted: (data) => {
            const url = data?.getReportUrl;
            if (url) window.open(url, '_blank');
        },
        onError: handleError('Błąd ściągania formularza'),
    });

    const [getReport, { data }] = useGetReportLazyQuery({
        variables: { recordingId },
        onError: handleError('Błąd ściągania formularza'),
    });

    const [requestReport] = useRequestReportMutation({
        variables: { recordingId },
        onError: handleError('Błąd generowania formularza'),
        refetchQueries: 'active',
    });

    const cleanedData = useRemoveTypename(data?.getReport);

    useEffect(() => {
        getReport();
    }, []);

    const renderLoader = () => (
        <Tooltip text="Formularz jest generowany">
            <RotatingLines
                strokeColor="#545454"
                strokeWidth="2.5"
                width="26.5"
            />
        </Tooltip>
    );

    const renderGenerateButton = () => (
        <Tooltip text="Generuj formularz">
            <IconManager
                cursorPointer
                name="GenerateIcon"
                onClick={() => requestReport()}
            />
        </Tooltip>
    );

    const renderDownloadButton = () => (
        <Tooltip text="Ściągnij formularz">
            <IconManager
                cursorPointer
                name="TableFormIcon"
                onClick={getReportUrl}
            />
        </Tooltip>
    );

    const renderOpenFormButton = () => (
        <Tooltip text="Wyswietl formularz">
            <IconManager
                cursorPointer
                name={
                    THEME_VARIANT === 'DOCTOR'
                        ? 'DoctorTableFormIcon'
                        : 'TableFormIcon'
                }
                onClick={() => {
                    dispatch(setFormData(cleanedData));
                    dispatch(setRecordingId(recordingId));
                }}
            />
        </Tooltip>
    );

    return (
        <FormWrapper>
            {THEME_VARIANT !== 'DOCTOR' && (
                <>
                    {!hasReport
                        ? isReportQueued
                            ? renderLoader()
                            : renderGenerateButton()
                        : renderDownloadButton()}
                </>
            )}

            {THEME_VARIANT === 'DOCTOR' && isReportQueued && renderLoader()}

            {hasReport ? renderOpenFormButton() : '-'}
        </FormWrapper>
    );
};
